import React, { useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Sheet, SheetContentContained, SheetTitle } from "../../../components/ui/sheet";
import { useDispatch, useSelector } from 'react-redux';
import ReorderTable from '../../../components/ReorderTable';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { Label } from '../../../components/ui/label';
import SelectInput from '../../../components/SelectInput';
import { MultiSelect } from '../../../components/ui/multiselect';
import { useExtractionContext } from '../../../contexts/extractions/ExtractionContext';
import { useExtCustomColumns } from '../../../contexts/extractions/ExtCustomColumnContext';
import { useExtCustomColumnQueries } from '../../../contexts/extractions/ExtCustomColumnQueryContext';
import { createExtCustomColumnQuery, updateExtCustomColumnQuery } from '../../../store/extCustomColumnQueries/actions';
import QueryBuilder from '../step2/QueryBuilder';
import { setOpenModal } from '../../../store/extCustomColumnQueries/actions';
import { setLoading } from '../../../store/extCustomColumns/actions';


const AddCustomColumns = () => {
  const dispatch = useDispatch();
  const { fieldValueOptionsMap } = useExtractionContext();
  const {
    queryBuilderOptions,
    handleInputChange,
    saveExtCustomColumn,
    addNewExtCustomColumnQuery,
    setOpenCustomColumnDrawer,
    replaceOptions,
    fetchReplaceOptions,
    filteredCustomColumnOptions,
  } = useExtCustomColumns();

  const {
      table,
      columns,
      extCustomColumnQueries,
      showDeleteConfirmationDialog,
      handleDelete,
      handleCloseDeleteConfirmation,
      handleRowReorder,
      getRowId,
    } = useExtCustomColumnQueries();

  const { extractionViewOnly } = useSelector((state) => state.Extractions);
  const { loadingExtCustomColumns, extCustomColumn, errors: extCustomColumnErrors, openDrawer } = useSelector((state) => state.ExtCustomColumns);
  const { openModal, extCustomColumnQuery, loadingExtCustomColumnQueries } = useSelector((state) => state.ExtCustomColumnQueries);

  useEffect(() => {
    // Ensure loading is reset to false on mount
    dispatch(setLoading(false));
    dispatch(setOpenModal(false));
    return () => {
      dispatch(setOpenModal(false));
    };
  }, [dispatch]);
  
  return (
    <Sheet open={openDrawer} onOpenChange={(val) => setOpenCustomColumnDrawer(val)}>
      <SheetContentContained side="bottom" showCloseIcon={false} className="h-full">
        <SheetTitle className="sr-only">Custom Column</SheetTitle>
        <div className="mt-2">
          <div className="flex items-center justify-between">
            <div className="w-full space-y-6">
              {/* Header Section */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    Custom Column Name
                  </label>
                  <SelectInput
                    key="title"
                    fieldKey="customColumn"
                    type="map"
                    list={filteredCustomColumnOptions}
                    selectedValue={extCustomColumn?.id || extCustomColumn?.title || ''}
                    onChange={(value) => handleInputChange("title", value)}
                    placeholder="Custom Column Name"
                    allowAddToList={true}
                    // Pass undefined instead of false so the prop is never
                    // forwarded as a DOM attribute on the underlying <button>.
                    error={extCustomColumnErrors?.title ? extCustomColumnErrors.title : undefined}
                    disabled={loadingExtCustomColumns}
                  />
                  {extCustomColumnErrors?.title && (
                    <small className="text-destructive text-xs mt-1">{extCustomColumnErrors.title}</small>
                  )}
                </div>

                <div>
                  <Label className="block text-sm font-medium text-slate-600 mb-2">
                    Replace with existing column
                  </Label>
                  <MultiSelect
                    type="string"
                    key="replaceField"
                    options={replaceOptions}
                    value={extCustomColumn?.replace_with || []}
                    onValueChange={(value) => handleInputChange("replace_with", value)}
                    onSearchChange={(keyword) => fetchReplaceOptions(keyword)}
                    isLoading={loadingExtCustomColumns}
                    placeholder="Search and select..."
                    className="h-9 bg-white border-gray-300"
                    debounceMs={300}
                    showSearch={true}
                    // Pass undefined instead of false so the prop is never
                    // forwarded as a DOM attribute on the underlying <button>.
                    error={extCustomColumnErrors?.replace_with ? extCustomColumnErrors.replace_with : undefined}
                  />
                  {extCustomColumnErrors?.replace_with && (
                    <small className="text-destructive text-xs mt-1">{extCustomColumnErrors.replace_with}</small>
                  )}
                </div>
              </div>

              {/* Groups Section */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-base font-medium text-slate-700">
                    Please define the groups/categories for this Custom Column
                  </h3>
                  <Button 
                    variant="border_outline"
                    onClick={addNewExtCustomColumnQuery}
                    disabled={extractionViewOnly || (!extCustomColumn?.title && !extCustomColumn?.replace_with)}
                  >
                    {!extCustomColumn.id ? "Create" : "Add Query"}
                  </Button>
                </div>

                {/* ReorderTable */}
                <ReorderTable
                  loading={loadingExtCustomColumnQueries}
                  table={table}
                  data={extCustomColumnQueries || []}
                  columns={columns}
                  enableRowReordering={true}
                  onRowReorder={handleRowReorder}
                  getRowId={getRowId}
                />
              </div>

              {/* Footer Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setOpenCustomColumnDrawer(false)}>
                  Cancel
                </Button>
                <Button onClick={saveExtCustomColumn} disabled={!extCustomColumn.id}>
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
        {openModal && (
          <QueryBuilder
            options={queryBuilderOptions}
            fieldDropdownOptions={fieldValueOptionsMap}
            editMode={!!extCustomColumnQuery}
            triggerText={extCustomColumnQuery ? "Edit" : "Add New Query"}
            titleText={extCustomColumnQuery ? "Edit Data Group" : "Add New Query"}
            nameLabel="Query Name"
            namePlaceholder="Enter query name"
            createAction={createExtCustomColumnQuery}
            updateAction={updateExtCustomColumnQuery}
            data={extCustomColumnQuery}
            openModal={openModal}
            disabled={extractionViewOnly}
            saveInProgress={loadingExtCustomColumnQueries}
          />
      )}

      <ConfirmDialog
        open={showDeleteConfirmationDialog}
        confirmAction={handleDelete}
        handleClose={handleCloseDeleteConfirmation}
        actionText="Delete"
        descriptionText="Are you sure you want to delete this custom column query?"
      />
      </SheetContentContained>
    </Sheet>
  );
};

export default AddCustomColumns;